/*  Copyright 2018 - 2021 Swiss Federal Institute of Technology Lausanne (EPFL)
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *  http://www.apache.org/licenses/LICENSE-2.0.
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 *
 *  This open source software code was developed in part or in whole in the
 *  Human Brain Project, funded from the European Union's Horizon 2020
 *  Framework Programme for Research and Innovation under
 *  Specific Grant Agreements No. 720270, No. 785907, and No. 945539
 *  (Human Brain Project SGA1, SGA2 and SGA3).
 *
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 *
 */
import { AxiosInstance } from "axios";
import API, { Settings, Instance } from "./API";

const RELATIVE_ROOT_PATH = "/editor/api";

const endpoints = {
  settings: () => 
    `${RELATIVE_ROOT_PATH}/settings`,
  initializeSpace: (space: string) =>
    `${RELATIVE_ROOT_PATH}/spaces/${encodeURIComponent(space)}/initialize`,
  instance: (instanceId: string) => 
    `${RELATIVE_ROOT_PATH}/instances/${instanceId}/raw`,
  createInstance: (instanceId: string, space: string) => 
    `${RELATIVE_ROOT_PATH}/instances/${instanceId}?space=${encodeURIComponent(space)}`
};

class APIBackendAdapter implements API {
  private _axios: AxiosInstance;

  constructor(axios: AxiosInstance) {
    this._axios = axios;
  }
  
  async getSettings(): Promise<Settings> {
    const { data } = await this._axios.get(endpoints.settings());
    return data?.data as Settings;
  }

  async initializeSpace(space: string, types?: string[]): Promise<void> {
    await this._axios.post(endpoints.initializeSpace(space), types);
  }

  async getInstance(instanceId: string): Promise<Instance> {
    const { data } =  await this._axios.get(endpoints.instance(instanceId));
    return data as Instance;
  }

  async createInstance(instanceId: string, space: string, schemas: string[]): Promise<void> {
    const payload = {
      "@type": schemas,
    };
    await this._axios.post(endpoints.createInstance(instanceId, space), payload);
  }
}

export default APIBackendAdapter;
